<template>
  <div class="card card-custom grupo-empresa">
    <div class="card-header border-0 pt-6">
      <div class="card-title w-100 justify-content-start text-left">
        <div>
          <h3 class="card-label mb-1">Multiempresas</h3>
          <span class="text-muted font-size-sm">
            Organize matriz, filiais e acessos dos usuários.
          </span>
        </div>
      </div>
    </div>

    <div class="card-body pt-4">
      <div v-if="carregando" class="text-center py-10">
        <span class="spinner spinner-primary"></span>
      </div>

      <template v-else-if="!grupo">
        <div class="alert alert-light-primary">
          Esta empresa ainda não pertence a um grupo empresarial. Ela será a
          matriz do novo grupo.
        </div>
        <b-form @submit.prevent="criarGrupo">
          <b-form-group label="Nome do grupo" label-for="nome-grupo">
            <b-form-input
              id="nome-grupo"
              v-model.trim="novoGrupoNome"
              maxlength="150"
              placeholder="Ex.: Grupo Empresarial"
              required
            />
          </b-form-group>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="salvando || !novoGrupoNome"
          >
            <span
              v-if="salvando"
              class="spinner spinner-sm spinner-light mr-2"
            ></span>
            Criar grupo empresarial
          </button>
        </b-form>
      </template>

      <template v-else>
        <div
          class="d-flex flex-wrap align-items-center justify-content-between mb-6"
        >
          <div>
            <h4 class="mb-1">{{ grupo.nome }}</h4>
            <span
              class="label label-inline"
              :class="
                grupo.status === 'A'
                  ? 'label-light-success'
                  : 'label-light-danger'
              "
            >
              {{ grupo.status === "A" ? "Ativo" : "Inativo" }}
            </span>
            <span class="label label-inline label-light-primary ml-2">
              {{ grupo.empresas_ativas || 0 }} de
              {{ grupo.limite_empresas || 0 }} empresas
            </span>
          </div>
          <div v-if="podeGerenciar" class="d-flex align-items-center">
            <router-link
              v-if="grupo.status === 'A'"
              :to="{ name: 'grupoEmpresaPendencias' }"
              class="btn btn-sm btn-light-warning mr-2"
            >
              <i class="fas fa-exclamation-triangle mr-1"></i>
              Central de pendências
            </router-link>
            <router-link
              v-if="grupo.status === 'A'"
              :to="{ name: 'grupoEmpresaConsolidado' }"
              class="btn btn-sm btn-light-primary mr-2"
            >
              <i class="fas fa-chart-bar mr-1"></i>
              Painel consolidado
            </router-link>
            <router-link
              :to="{ name: 'grupoEmpresaAuditoria' }"
              class="btn btn-sm btn-light-primary mr-2"
            >
              <i class="fas fa-history mr-1"></i>
              Auditoria
            </router-link>
            <button
              type="button"
              class="btn btn-sm"
              :class="
                grupo.status === 'A' ? 'btn-light-danger' : 'btn-light-success'
              "
              :disabled="salvando"
              @click="alternarStatusGrupo"
            >
              {{ grupo.status === "A" ? "Desativar grupo" : "Ativar grupo" }}
            </button>
          </div>
        </div>

        <div v-if="!podeGerenciar" class="alert alert-light-warning">
          A administração do grupo deve ser realizada acessando a empresa
          matriz.
        </div>

        <section class="mb-10">
          <div
            class="d-flex flex-wrap align-items-center justify-content-between mb-4"
          >
            <h5 class="mb-0">Empresas do grupo</h5>
            <button
              v-if="podeGerenciar && grupo.status === 'A'"
              type="button"
              class="btn btn-sm btn-primary"
              :disabled="salvando || cadastrandoFilial || limiteAtingido"
              @click="abrirCadastroFilial"
            >
              <i class="fas fa-plus mr-1"></i>
              {{ limiteAtingido ? "Limite atingido" : "Cadastrar nova filial" }}
            </button>
          </div>
          <div v-if="limiteAtingido" class="alert alert-light-warning py-2 px-3">
            O grupo atingiu o limite de empresas liberado no Multiempresa.
          </div>
          <div class="table-responsive">
            <table class="table table-vertical-center">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Documento</th>
                  <th>Tipo</th>
                  <th v-if="podeGerenciar" class="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="membro in grupo.membros"
                  :key="membro.id_grupo_empresa_membro"
                >
                  <td>
                    <strong>{{ nomeEmpresa(membro.empresa) }}</strong>
                  </td>
                  <td>{{ membro.empresa && membro.empresa.cnpj_cpf }}</td>
                  <td>
                    <span
                      class="label label-inline"
                      :class="
                        membro.tipo === 'MATRIZ'
                          ? 'label-light-primary'
                          : 'label-light-info'
                      "
                    >
                      {{ membro.tipo === "MATRIZ" ? "Matriz" : "Filial" }}
                    </span>
                  </td>
                  <td v-if="podeGerenciar" class="text-right">
                    <button
                      v-if="membro.tipo !== 'MATRIZ'"
                      type="button"
                      class="btn btn-sm btn-light-danger"
                      :disabled="salvando"
                      @click="removerEmpresa(membro)"
                    >
                      Remover
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section v-if="podeGerenciar" data-testid="acessos-usuarios-matriz">
          <div class="d-flex align-items-center justify-content-between mb-4">
            <h5 class="mb-0">Acesso dos usuários do grupo</h5>
            <button
              type="button"
              class="btn btn-sm btn-light-primary"
              :disabled="carregandoUsuarios"
              @click="carregarUsuarios"
            >
              Atualizar
            </button>
          </div>

          <div v-if="carregandoUsuarios" class="text-center py-6">
            <span class="spinner spinner-primary"></span>
          </div>
          <div
            v-else-if="!usuariosConfiguraveis.length"
            class="alert alert-light-primary"
          >
            Nenhum usuário ativo da matriz ou das filiais disponível para
            configurar o acesso. Cadastre o usuário em uma empresa do grupo e
            clique em Atualizar.
          </div>
          <div v-else class="row">
            <div
              v-for="usuario in usuariosConfiguraveis"
              :key="usuario.id_emp_usuario"
              class="col-xl-6 mb-5"
            >
              <div class="border rounded p-5 h-100">
                <div class="d-flex justify-content-between mb-4">
                  <div>
                    <strong class="d-block">{{ usuario.nome }}</strong>
                    <small class="text-muted">{{ usuario.email }}</small>
                    <small class="text-muted d-block">
                      Origem: {{ usuario.empresa_origem }}
                    </small>
                  </div>
                  <span
                    v-if="usuario.admin"
                    class="label label-light-primary label-inline"
                  >
                    Administrador
                  </span>
                </div>

                <b-form-group label="Escopo de acesso">
                  <b-form-select
                    v-model="usuario.escopo_acesso"
                    :options="opcoesEscopo"
                    :disabled="grupo.status !== 'A' || usuario.salvando"
                    @change="normalizarEmpresasUsuario(usuario)"
                  />
                </b-form-group>

                <div
                  v-if="usuario.escopo_acesso === 'EMPRESAS_SELECIONADAS'"
                  class="mb-4"
                >
                  <label class="font-weight-bold">Empresas permitidas</label>
                  <b-form-checkbox-group
                    v-model="usuario.empresas"
                    :options="opcoesEmpresasGrupo(usuario)"
                    stacked
                    :disabled="grupo.status !== 'A' || usuario.salvando"
                  />
                </div>

                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  :disabled="!podeSalvarUsuario(usuario)"
                  @click="salvarAcessoUsuario(usuario)"
                >
                  <span
                    v-if="usuario.salvando"
                    class="spinner spinner-sm spinner-light mr-2"
                  ></span>
                  Salvar acesso
                </button>
              </div>
            </div>
          </div>
        </section>
      </template>
    </div>

    <b-modal
      id="modal-cadastrar-filial"
      title="Cadastrar nova filial"
      hide-footer
      centered
      size="lg"
      @hidden="limparFormularioFilial"
    >
      <div class="alert alert-light-primary">
        A filial terá uma licença, configurações e dados próprios. Nada será
        copiado da matriz.
      </div>

      <b-form @submit.prevent="cadastrarFilial">
        <div class="row">
          <b-form-group
            class="col-md-7"
            label="Razão social"
            label-for="filial-nome"
          >
            <b-form-input
              id="filial-nome"
              v-model.trim="formFilial.nome"
              maxlength="70"
              required
            />
          </b-form-group>

          <b-form-group
            class="col-md-5"
            label="CPF/CNPJ"
            label-for="filial-documento"
          >
            <b-form-input
              id="filial-documento"
              v-mask="['###.###.###-##', '##.###.###/####-##']"
              v-model.trim="formFilial.cnpj_cpf"
              inputmode="numeric"
              maxlength="18"
              required
            />
          </b-form-group>

          <b-form-group
            class="col-md-6"
            label="Nome fantasia"
            label-for="filial-fantasia"
          >
            <b-form-input
              id="filial-fantasia"
              v-model.trim="formFilial.nome_fantasia"
              maxlength="100"
            />
          </b-form-group>

          <b-form-group
            class="col-md-6"
            label="Responsável"
            label-for="filial-responsavel"
          >
            <b-form-input
              id="filial-responsavel"
              v-model.trim="formFilial.responsavel"
              maxlength="100"
            />
          </b-form-group>

          <b-form-group
            class="col-md-7"
            label="E-mail da filial"
            label-for="filial-email"
          >
            <b-form-input
              id="filial-email"
              v-model.trim="formFilial.email"
              type="email"
              maxlength="100"
            />
          </b-form-group>

          <b-form-group
            class="col-md-5"
            label="Telefone"
            label-for="filial-fone"
          >
            <b-form-input
              id="filial-fone"
              v-mask="['(##) #### ####', '(##) # #### ####']"
              v-model.trim="formFilial.fone"
              inputmode="numeric"
              maxlength="16"
            />
          </b-form-group>
        </div>

        <div class="d-flex justify-content-end mt-3">
          <button
            type="button"
            class="btn btn-light mr-2"
            :disabled="cadastrandoFilial"
            @click="$bvModal.hide('modal-cadastrar-filial')"
          >
            Cancelar
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="
              cadastrandoFilial || !formFilial.nome || !formFilial.cnpj_cpf
            "
          >
            <span
              v-if="cadastrandoFilial"
              class="spinner spinner-sm spinner-light mr-2"
            ></span>
            {{ cadastrandoFilial ? "Cadastrando..." : "Cadastrar filial" }}
          </button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { mask } from "vue-the-mask";

export default {
  name: "GrupoEmpresaIndex",
  directives: {
    mask,
  },
  data() {
    return {
      carregando: true,
      carregandoUsuarios: false,
      salvando: false,
      cadastrandoFilial: false,
      grupo: null,
      usuarios: [],
      novoGrupoNome: "",
      formFilial: {
        nome: "",
        nome_fantasia: "",
        cnpj_cpf: "",
        responsavel: "",
        email: "",
        fone: "",
      },
      opcoesEscopo: [
        { value: "SOMENTE_EMPRESA", text: "Somente empresa de origem" },
        { value: "EMPRESAS_SELECIONADAS", text: "Empresas selecionadas" },
        { value: "TODO_GRUPO", text: "Todo o grupo" },
      ],
    };
  },
  computed: {
    ...mapGetters(["currentUser", "empresaContexto"]),
    empresaAtualId() {
      return Number(this.empresaContexto?.id_empresa || 0);
    },
    podeGerenciar() {
      return (
        Boolean(this.currentUser?.admin) &&
        Number(this.grupo?.id_empresa_matriz) === this.empresaAtualId
      );
    },
    limiteAtingido() {
      const limite = Number(this.grupo?.limite_empresas || 0);
      const utilizadas = Number(
        this.grupo?.empresas_ativas || this.grupo?.membros?.length || 0
      );

      return limite > 0 && utilizadas >= limite;
    },
    usuariosConfiguraveis() {
      return this.usuarios.filter(
        (usuario) =>
          usuario.status === "A" &&
          !(
            usuario.admin &&
            Number(usuario.id_empresa_origem) ===
              Number(this.grupo?.id_empresa_matriz)
          )
      );
    },
  },
  methods: {
    opcoesEmpresasGrupo(usuario) {
      return (this.grupo?.membros || [])
        .filter(
          (membro) =>
            membro.ativo !== false &&
            Number(membro.id_empresa) !== Number(usuario.id_empresa_origem)
        )
        .map((membro) => ({
          value: Number(membro.id_empresa),
          text: this.nomeEmpresa(membro.empresa),
        }));
    },
    nomeEmpresa(empresa) {
      return (
        empresa?.nome_fantasia ||
        empresa?.nome ||
        `Empresa ${empresa?.id_empresa}`
      );
    },
    async carregarGrupo() {
      this.carregando = true;

      try {
        const { data } = await this.$http.get("/empresa/grupos/atual");
        this.grupo = data?.data || null;

        if (this.podeGerenciar) {
          await this.carregarUsuarios();
        }
      } finally {
        this.carregando = false;
      }
    },
    async criarGrupo() {
      if (!this.novoGrupoNome || this.salvando) return;
      this.salvando = true;

      try {
        const { data } = await this.$http.post("/empresa/grupos", {
          nome: this.novoGrupoNome,
        });
        this.grupo = data.data;
        this.novoGrupoNome = "";
        this.$toast.success("Grupo empresarial criado.");
        await this.carregarUsuarios();
      } finally {
        this.salvando = false;
      }
    },
    abrirCadastroFilial() {
      this.formFilial.responsavel = this.currentUser?.nome || "";
      this.$bvModal.show("modal-cadastrar-filial");
    },
    limparFormularioFilial() {
      this.formFilial = {
        nome: "",
        nome_fantasia: "",
        cnpj_cpf: "",
        responsavel: "",
        email: "",
        fone: "",
      };
    },
    async cadastrarFilial() {
      if (
        this.cadastrandoFilial ||
        !this.formFilial.nome ||
        !this.formFilial.cnpj_cpf
      ) {
        return;
      }

      this.cadastrandoFilial = true;

      try {
        const { data } = await this.$http.post(
          `/empresa/grupos/${this.grupo.id_grupo_empresa}/filiais`,
          this.formFilial
        );
        this.grupo = data.data.grupo;
        await this.$store.dispatch("loadEmpresas");
        this.$bvModal.hide("modal-cadastrar-filial");
        this.$toast.success("Filial cadastrada e adicionada ao grupo.");
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          "Não foi possível cadastrar a filial.";
        this.$toast.error(message);
      } finally {
        this.cadastrandoFilial = false;
      }
    },
    async removerEmpresa(membro) {
      const result = await this.$swal.fire({
        icon: "warning",
        title: `Remover ${this.nomeEmpresa(membro.empresa)} do grupo?`,
        text: "Os acessos herdados para esta filial serão removidos.",
        showCancelButton: true,
        confirmButtonText: "Remover",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#f64e60",
      });

      if (!result.isConfirmed) return;
      this.salvando = true;

      try {
        const { data } = await this.$http.delete(
          `/empresa/grupos/${this.grupo.id_grupo_empresa}/empresas/${membro.id_empresa}`
        );
        this.grupo = data.data;
        await this.carregarUsuarios();
        this.$toast.success("Filial removida do grupo.");
      } finally {
        this.salvando = false;
      }
    },
    async alternarStatusGrupo() {
      const novoStatus = this.grupo.status === "A" ? "I" : "A";
      this.salvando = true;

      try {
        const { data } = await this.$http.put(
          `/empresa/grupos/${this.grupo.id_grupo_empresa}`,
          { status: novoStatus }
        );
        this.grupo = data.data;
        this.$toast.success(
          novoStatus === "A" ? "Grupo ativado." : "Grupo desativado."
        );
      } finally {
        this.salvando = false;
      }
    },
    async carregarUsuarios() {
      if (!this.grupo?.id_grupo_empresa || !this.podeGerenciar) return;
      this.carregandoUsuarios = true;

      try {
        const { data } = await this.$http.get(
          `/empresa/grupos/${this.grupo.id_grupo_empresa}/usuarios`
        );
        this.usuarios = (data?.data || []).map((usuario) => ({
          ...usuario,
          empresas: (usuario.empresas || []).map(Number),
          salvando: false,
        }));
      } finally {
        this.carregandoUsuarios = false;
      }
    },
    normalizarEmpresasUsuario(usuario) {
      if (usuario.escopo_acesso !== "EMPRESAS_SELECIONADAS") {
        usuario.empresas = [];
      }
    },
    podeSalvarUsuario(usuario) {
      if (this.grupo.status !== "A" || usuario.salvando) return false;

      return (
        usuario.escopo_acesso !== "EMPRESAS_SELECIONADAS" ||
        usuario.empresas.length > 0
      );
    },
    async salvarAcessoUsuario(usuario) {
      if (!this.podeSalvarUsuario(usuario)) return;
      this.$set(usuario, "salvando", true);

      try {
        await this.$http.put(
          `/empresa/grupos/${this.grupo.id_grupo_empresa}/usuarios/${usuario.id_usuario}/acesso`,
          {
            id_empresa_origem: usuario.id_empresa_origem,
            escopo_acesso: usuario.escopo_acesso,
            empresas: usuario.empresas,
          }
        );
        this.$toast.success(`Acesso de ${usuario.nome} atualizado.`);
      } finally {
        this.$set(usuario, "salvando", false);
      }
    },
  },
  mounted() {
    this.carregarGrupo();
  },
};
</script>

<style scoped>
.grupo-empresa .table td,
.grupo-empresa .table th {
  vertical-align: middle;
}
</style>
